<template>
  <div class="container">
    <div v-if="showOneOzSaleMessage" class="row one-ounce-sale-message">
      <div class="col-md-12">
        <p>
          <strong>NOTE:</strong>
          All 1 oz fragrance oils are on sale for
          <strong>{{ oneOzSalePrice }}</strong> each. Limit of
          <strong>{{ oneOzLimitAsWords }}</strong> per fragrance.
        </p>
      </div>
    </div>
    <form @submit.prevent="submit">
      <div class="row" style="margin-bottom: 20px">
        <div class="col-md-6 form-inline">
          <taxon-select
            :taxon-groups="taxonGroups"
            blank-text="-- Change Product Category --"
            :selected-taxon="taxon"
            @change="loadProducts"
          />
        </div>
        <div class="col-xs-6 col-md-2">
          <img
            v-show="loading"
            :src="loadingImageUrl"
            alt="loading"
            class="taxon-loading-indicator"
          />
        </div>
        <div v-if="products.length" class="col-xs-6 col-md-4 text-right">
          <submit-button
            :loading="submitting"
            class="btn-lg quick-order-add-to-cart"
            default-label="Add to Cart"
            loading-label="Adding to Cart ..."
          />
          <alert-box
            v-for="alert in alerts"
            :key="alert.message"
            :type="alert.type"
          >
            {{ alert.message }}
          </alert-box>
        </div>
      </div>
      <div v-if="products.length" class="quick-order-products">
        <quick-order-product
          v-for="product in products"
          :key="product.id"
          :product="product"
          :one-oz-limit="oneOzLimit"
          :one-oz-limit-as-words="oneOzLimitAsWords"
          @change="onQuantityChange"
        />
      </div>
      <div v-else>
        <h6>
          No products were found in this category.
        </h6>
      </div>
      <div v-if="products.length" class="row quick-order-bottom-row">
        <div class="col-xs-12 text-right">
          <submit-button
            :loading="submitting"
            class="btn-lg quick-order-add-to-cart"
            default-label="Add to Cart"
            loading-label="Adding to Cart ..."
          />
          <alert-box
            v-for="alert in alerts"
            :key="alert.message"
            :message="alert.message"
            :type="alert.type"
          />
        </div>
      </div>
    </form>
    <add-to-cart-modal
      ref="modal"
      :title="responseData.title"
      :order="responseData.order"
      :warnings="responseData.warnings"
    />
  </div>
</template>

<script>
  import axios from "axios"
  import map from "lodash/map"
  import sumBy from "lodash/sumBy"
  import isEmpty from "lodash/isEmpty"
  import updateCart from "mixins/storefront/update-cart"
  import alerts from "mixins/storefront/cart-alerts"
  import TaxonSelect from "./quick-order/taxon-select"
  import QuickOrderProduct from "./quick-order/product"
  import SubmitButton from "./submit-button"
  import AddToCartModal from "./add-to-cart/modal"

  export default {
    components: {
      TaxonSelect,
      QuickOrderProduct,
      SubmitButton,
      AddToCartModal,
    },
    mixins: [updateCart, alerts],
    props: {
      cartPath: {
        type: String,
        default: "/cart",
      },
      quickOrderPath: {
        type: String,
        default: "/quick_order",
      },
      taxonGroups: {
        type: Array,
        default: () => [],
      },
      initialTaxon: {
        type: Object,
        default: null,
      },
      oneOzLimit: {
        type: Number,
        default: null,
      },
      oneOzLimitAsWords: {
        type: String,
        default: "one bottle",
      },
      oneOzSalePrice: {
        type: String,
        default: "99 cents",
      },
      initialProducts: {
        type: Array,
        required: true,
      },
      loadingImageUrl: {
        type: String,
        required: true,
      },
      initialPageTitle: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        loading: false,
        submitting: false,
        order: {},
        responseData: {
          order: {},
        },
        products: this.initialProducts || [],
        taxon: this.initialTaxon,
        pageTitle: this.initialPageTitle,
      }
    },
    computed: {
      showOneOzSaleMessage() {
        return this.oneOzLimit && this.taxon && this.taxon.fragrance
      },
    },
    watch: {
      taxon: {
        immediate: true,
        handler(taxon) {
          this.$emit("taxonloaded", taxon)
        },
      },
    },
    created() {
      // Customer navigates with history back and forth buttons
      window.onpopstate = function onWindowPopState(event) {
        if (event.state) {
          this.taxon = event.state.taxon
          this.products = event.state.products
          this.pageTitle = event.state.pageTitle
        } else {
          this.taxon = this.initialTaxon
          this.products = this.initialProducts
          this.pageTitle = this.initialPageTitle
        }
        this.updatePageTitle()
        this.order = {}
      }.bind(this)
    },
    methods: {
      loadProducts(value) {
        if (value === "") {
          return
        }

        const url = `${this.quickOrderPath}/${value}`
        this.loading = true

        axios
          .get(url, { headers: { Accept: "application/json" } })
          .then((response) => {
            this.loading = false
            switch (response.status) {
              case 200:
                this.taxon = response.data.taxon
                this.products = response.data.products
                // Push the new state into the browser history
                // so customers can navigate with back and forth buttons
                if (this.taxon) {
                  this.updateBrowserState(
                    response.data.page_title,
                    `/quick_order/${value}`
                  )
                }
                break
              case 422:
                this.alert(response.data.message, "danger")
                break
              default:
                break
            }
          })
          .catch((error) => {
            this.loading = false
            this.alert(error.message, "danger")
          })
        this.order = {}
      },
      onQuantityChange(payload) {
        this.$set(this.order, payload.variantId, payload.quantity)
      },
      submit() {
        if (isEmpty(this.order)) {
          return
        }

        this.alerts = []
        this.submitting = true

        axios
          .post(this.cartPath, { variants: this.order })
          .then((response) => {
            this.submitting = false

            switch (response.status) {
              case 200:
                this.responseData = response.data
                this.$refs.modal.open()
                this.updateCart(response.data.order)
                this.csTrack(response.data.items_added)
                break
              case 422:
                this.alert(response.data.message, "danger")
                break
              default:
                break
            }

            this.order = {}
          })
          .catch((error) => {
            this.submitting = false
            this.alert(error.message, "danger")
          })
      },
      csTrack(itemsAdded) {
        cs.dataLayer.push({
          event: "addToCart",
          ecommerce: {
            add: {
              products: itemsAdded.map((item) => {
                return {
                  id: item.variant.sku,
                  name: item.variant.product_name,
                  category: item.variant.ga_category,
                  variant: item.variant.name,
                  price: item.variant.price,
                  quantity: item.quantity,
                }
              }),
            },
          },
          skus_selected: map(itemsAdded, (item) => item.variant.sku),
          total_value_added_to_cart: sumBy(itemsAdded, (item) => {
            return item.quantity * item.variant.price
          }),
        })
      },
      updateBrowserState(pageTitle, url) {
        this.pageTitle = pageTitle
        window.history.pushState(this.$data, this.pageTitle, url)
        this.updatePageTitle()
      },
      updatePageTitle() {
        document.querySelector("title").innerText = this.pageTitle
      },
    },
  }
</script>

<style>
  .quick-order-bottom-row {
    margin-top: 20px;
  }
</style>
